<template>
  <div ref="mapEl" class="free-map"></div>
</template>

<script>
// Free, keyless replacement for the old vue3-google-map based map:
// OpenStreetMap tiles via Leaflet. No API key or billing account needed.
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "FreeMap",
  props: {
    center: { type: Object, required: true }, // { lat, lng }
    zoom: { type: Number, default: 12.5 },
    markers: { type: Array, default: () => [] }, // [{ lat, lng, address_displayed, address_url, conv }]
    centerMarker: { type: Object, default: null }, // { lat, lng } current-location pin
    openIndex: { type: [Number, String], default: null },
    // When true, markers dim and popups show "Comming Soon" for position.conv === false
    // (only HospitalInso.vue's data distinguishes conventioned vs non-conventioned).
    convGated: { type: Boolean, default: false },
  },
  emits: ["marker-click", "popup-close"],
  data() {
    return {
      map: null,
      leafletMarkers: [],
      centerMarkerLayer: null,
    };
  },
  watch: {
    center(val) {
      if (this.map && val && Number.isFinite(val.lat) && Number.isFinite(val.lng)) {
        this.map.setView([val.lat, val.lng]);
      }
    },
    markers: {
      handler() {
        this.renderMarkers();
      },
      deep: true,
    },
    centerMarker: {
      handler() {
        this.renderCenterMarker();
      },
      deep: true,
    },
    openIndex(index) {
      this.syncOpenPopup(index);
    },
  },
  mounted() {
    this.map = L.map(this.$refs.mapEl, { zoomControl: true }).setView(
      [this.center.lat || 0, this.center.lng || 0],
      this.zoom
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    this.renderMarkers();
    this.renderCenterMarker();
  },
  beforeUnmount() {
    if (this.map) this.map.remove();
  },
  methods: {
    popupHtml(position) {
      if (this.convGated && !position.conv) {
        return `<div class="free-map-popup free-map-popup-soon">Comming Soon</div>`;
      }
      return `<div class="free-map-popup">
        <div class="free-map-popup-address">${position.address_displayed ?? ""}</div>
        <a class="free-map-popup-link" href="${position.address_url ?? "#"}" target="_blank" rel="noopener">Ouvrir sur map</a>
      </div>`;
    },
    renderMarkers() {
      if (!this.map) return;
      this.leafletMarkers.forEach((m) => this.map.removeLayer(m));

      this.leafletMarkers = this.markers
        .filter((position) => Number.isFinite(position.lat) && Number.isFinite(position.lng))
        .map((position, index) => {
          const icon = L.icon({
            iconUrl: "http://admin-assurance-private.assuranceiso.com/web/marker.png",
            iconSize: [30, 30],
          });

          const opacity = this.convGated && !position.conv ? 0.55 : 1;
          const marker = L.marker([position.lat, position.lng], { icon, opacity }).addTo(this.map);

          const el = marker.getElement();
          if (el) {
            el.id = "mark_" + index;
            el.classList.add("mapmarker");
          }

          marker.bindPopup(this.popupHtml(position));
          marker.on("click", () => this.$emit("marker-click", { position, index }));
          marker.on("popupclose", () => this.$emit("popup-close"));

          return marker;
        });

      this.syncOpenPopup(this.openIndex);
    },
    renderCenterMarker() {
      if (!this.map) return;
      if (this.centerMarkerLayer) {
        this.map.removeLayer(this.centerMarkerLayer);
        this.centerMarkerLayer = null;
      }
      if (this.centerMarker && Number.isFinite(this.centerMarker.lat) && Number.isFinite(this.centerMarker.lng)) {
        this.centerMarkerLayer = L.marker(
          [this.centerMarker.lat, this.centerMarker.lng],
          {
            icon: L.icon({
              iconUrl: "http://admin-assurance-private.assuranceiso.com/web/location.png",
              iconSize: [30, 30],
            }),
          }
        ).addTo(this.map);
      }
    },
    syncOpenPopup(index) {
      const marker = this.leafletMarkers[Number(index)];
      if (index !== null && index !== undefined && marker) {
        marker.openPopup();
      }
    },
  },
};
</script>

<style>
.free-map {
  width: 100%;
  height: 100%;
}
.free-map-popup {
  min-width: 160px;
  text-align: center;
}
.free-map-popup-address {
  font-weight: bold;
  margin-bottom: 8px;
}
.free-map-popup-link {
  display: inline-block;
  background: #22c7e5;
  color: #fff;
  font-weight: bold;
  padding: 6px 14px;
  border-radius: 6px;
}
.free-map-popup-soon {
  padding: 4px 0;
}
</style>
